import {
  checkIcon,
  sortHighToLowIcon,
  sortLowToHighIcon,
  sortNewToOldIcon,
  sortOldToNewIcon,
} from '@/shared/ui/assets';
import { CustomButton } from '@/shared/ui/components';
import { Dropdown } from 'antd';
import classNames from 'classnames';
import React from 'react';
import { ReactSVG } from 'react-svg';
import { SORT_INBOX_KEYS } from '@/entities/inbox';
import { SORT_INBOX_TEXT, SORT_INBOX_TOOLTIP_TEXT } from '../../config/inbox';
import { useSortInbox } from './useSortInbox';
import type { SortInboxProps } from './sortInbox.types';

/**
 * Stands in as the default sort handler for the inbox sort menu when no callback is supplied. Takes no arguments and performs no work.
 *
 * @returns Nothing.
 */
const NOOP = () => {};

const SortInbox = ({
  onChangeSort = NOOP,
  disabled,
  hasPriority = true,
  sortValue,
}: SortInboxProps) => {
  const { displayIcon } = useSortInbox({ sortValue });

  /**
   * Handles a click on an inbox sort menu entry by forwarding the chosen sort to onChangeSort. Reads the key off the menu event object and returns nothing.
   *
   * @param key - Key of the clicked sort menu item.
   * @returns Nothing.
   */
  const onClickSort = ({ key }: { key: string }) => {
    onChangeSort(key);
  };
  /**
   * Reports whether a sort menu key is the sort currently applied to the inbox. Compares the key against the sortValue prop by strict equality.
   *
   * @param key - Sort menu key being tested.
   * @returns True when the key matches the current sort value.
   */
  const isActiveKey = (key: any) => sortValue === key;

  /**
   * The memoized antd dropdown item list for the inbox sort menu, covering the date-order options and, when priority sorting is allowed, the priority-order options. Each option carries its direction icon, the shared click handler, and a check mark rendered when the option matches the active sort value.
   *
   * @returns Dropdown menu configuration for the sort control.
   */
  const menu = React.useMemo(() => {
    const items = [
      {
        key: 'title',
        label: <span className="sort-inbox-title">Sort by date:</span>,
        disabled: true,
      },
      {
        key: SORT_INBOX_KEYS.NEW_TO_OLD,
        icon: <ReactSVG src={sortNewToOldIcon} className="sort-inbox-icon" />,
        // new syntax - antd 5.21+
        // extra: isActiveKey(SORT_INBOX_KEYS.NEW_TO_OLD) ? <ReactSVG src={checkIcon} className="sort-inbox-active-icon" /> : null,
        onClick: onClickSort,
        label: (
          <div className="sort-inbox-item">
            <div>{SORT_INBOX_TEXT.NEW_TO_OLD}</div>
            {isActiveKey(SORT_INBOX_KEYS.NEW_TO_OLD) && (
              <ReactSVG src={checkIcon} className="sort-inbox-active-icon" />
            )}
          </div>
        ),
      },
      {
        key: SORT_INBOX_KEYS.OLD_TO_NEW,
        icon: <ReactSVG src={sortOldToNewIcon} className="sort-inbox-icon" />,
        // extra: isActiveKey(SORT_INBOX_KEYS.OLD_TO_NEW) ? <ReactSVG src={checkIcon} className="sort-inbox-active-icon" /> : null,
        onClick: onClickSort,
        label: (
          <div className="sort-inbox-item">
            <div>{SORT_INBOX_TEXT.OLD_TO_NEW}</div>
            {isActiveKey(SORT_INBOX_KEYS.OLD_TO_NEW) && (
              <ReactSVG src={checkIcon} className="sort-inbox-active-icon" />
            )}
          </div>
        ),
      },
    ];
    if (hasPriority) {
      items.push(
        {
          key: 'title-2',
          label: <span className="sort-inbox-title">Sort by priority:</span>,
          disabled: true,
        },
        {
          key: SORT_INBOX_KEYS.HIGH_TO_LOW,
          icon: <ReactSVG src={sortHighToLowIcon} className="sort-inbox-icon" />,
          // extra: isActiveKey(SORT_INBOX_KEYS.HIGH_TO_LOW) ? <ReactSVG src={checkIcon} className="sort-inbox-active-icon" /> : null,
          onClick: onClickSort,
          label: (
            <div className="sort-inbox-item">
              <div>{SORT_INBOX_TEXT.HIGH_TO_LOW}</div>
              {isActiveKey(SORT_INBOX_KEYS.HIGH_TO_LOW) && (
                <ReactSVG src={checkIcon} className="sort-inbox-active-icon" />
              )}
            </div>
          ),
        },
        {
          key: SORT_INBOX_KEYS.LOW_TO_HIGH,
          icon: <ReactSVG src={sortLowToHighIcon} className="sort-inbox-icon" />,
          // extra: isActiveKey(SORT_INBOX_KEYS.LOW_TO_HIGH) ? <ReactSVG src={checkIcon} className="sort-inbox-active-icon" /> : null,
          onClick: onClickSort,
          label: (
            <div className="sort-inbox-item">
              <div>{SORT_INBOX_TEXT.LOW_TO_HIGH}</div>
              {isActiveKey(SORT_INBOX_KEYS.LOW_TO_HIGH) && (
                <ReactSVG src={checkIcon} className="sort-inbox-active-icon" />
              )}
            </div>
          ),
        },
      );
    }
    return { items };
  }, [hasPriority, sortValue]);

  return (
    <div className="sort-inbox-wrapper">
      <Dropdown
        menu={menu}
        trigger={['click']}
        overlayClassName="sort-inbox-dropdown"
        disabled={disabled}
      >
        <CustomButton
          id="sort-inbox-btn"
          style="neutral"
          type="outline"
          size="small"
          className="child-right"
          tooltipTitle={SORT_INBOX_TOOLTIP_TEXT[sortValue]}
          tooltipPlacement="bottom"
          disabled={disabled}
          tooltipClassName="sort-inbox-tooltip"
          ariaLabel={SORT_INBOX_TOOLTIP_TEXT[sortValue]}
          icon={
            <ReactSVG
              src={displayIcon}
              className={classNames(disabled ? '' : 'display-sort-icon')}
            />
          }
        />
      </Dropdown>
    </div>
  );
};

export default SortInbox;
